import React from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Wifi, Bell, Zap } from "lucide-react";
import { useInstallPrompt } from "@/lib/pwa";
import { useLang } from "@/context/LanguageContext";

export const InstallApp = () => {
  const { canInstall, installed, isIOS, promptInstall } = useInstallPrompt();
  const { t } = useLang();

  if (installed) return null;

  const perks = [
    { icon: Zap, label: t("install.perk1") },
    { icon: Wifi, label: t("install.perk2") },
    { icon: Bell, label: t("install.perk3") },
  ];

  const handleInstall = async () => {
    if (canInstall) { await promptInstall(); return; }
    toast.info(isIOS ? t("install.iosHint") : t("install.genericHint"));
  };

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-20" data-testid="install-app-section">
      <div className="relative overflow-hidden rounded-3xl bg-navy px-8 sm:px-14 py-14">
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-gold/10 blur-2xl" />
        <div className="relative grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-white/5 px-4 py-1.5 text-xs font-mono uppercase tracking-widest text-gold-light">
              {t("install.eyebrow")}
            </span>
            <h2 className="mt-6 font-serif text-3xl sm:text-4xl font-semibold text-white leading-tight">
              {t("install.title")} <span className="text-gold">{t("install.titleAccent")}</span> {t("install.titleEnd")}
            </h2>
            <p className="mt-4 text-white/70 max-w-lg">{t("install.desc")}</p>
            <ul className="mt-6 space-y-3">
              {perks.map((p) => (
                <li key={p.label} className="flex items-center gap-3 text-sm text-white/85">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gold/15 text-gold"><p.icon className="h-4.5 w-4.5" /></span>
                  {p.label}
                </li>
              ))}
            </ul>
            <div className="mt-8 space-y-3">
              <Button onClick={handleInstall} data-testid="install-app-btn" className="bg-gold text-navy hover:bg-gold-light font-semibold h-12 px-8 rounded-lg text-base">
                <Download className="mr-2 h-5 w-5" /> {t("install.btn")}
              </Button>
              {!canInstall && (
                <p className="text-sm text-white/70 max-w-md" data-testid={isIOS ? "install-ios-hint" : "install-generic-hint"}>
                  {isIOS ? t("install.iosHint") : t("install.genericHint")}
                </p>
              )}
            </div>
          </div>
          <div className="flex justify-center">
            <div className="relative">
              <div className="h-[300px] w-[150px] sm:h-[340px] sm:w-[170px] rounded-[2rem] border-[6px] border-white/15 bg-gradient-to-b from-white to-ivory shadow-2xl overflow-hidden flex flex-col items-center justify-center gap-4 p-6">
                <img src="/icon-512.png" alt="IMMOMAXE" className="h-24 w-24 rounded-2xl shadow-lg" />
                <div className="text-center">
                  <div className="font-serif text-lg font-bold text-navy">IMMOMAXE</div>
                  <div className="text-[11px] text-slate-500">Votre patrimoine, notre attention.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
